import { SeasonSelect } from "@/components/ui/season-select";
import React from "react";
import { ArrowLeft, Loader2, RefreshCw, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useColorSettings } from "@/contexts/ColorSettingsContext";
import { fetchFamilies, fetchSeasons } from "@/lib/api/manufacturing";
import {
  buildForecastSimulationItems,
  fetchAlfatexSimulationProducts,
  saveManufacturingSimulation,
  summarizeSimulationItems,
} from "@/lib/api/manufacturingSimulation";
import { SIMULATION_SIZES } from "@/lib/constants";

const cellKey = (color, size) => `${color}::${size}`;
const today = () => new Date().toISOString().slice(0, 10);
const todayLabel = () => new Date().toLocaleDateString("es-ES");
const trimCode = (value) => String(value || "").trim();
const emptySizeQuantities = () => Object.fromEntries(SIMULATION_SIZES.map((size) => [size, ""]));

function optionLabel(code, description) {
  const normalizedCode = trimCode(code);
  return description ? `${normalizedCode} · ${description}` : normalizedCode;
}

function normalizeQuantity(value) {
  const number = Math.floor(Number(value || 0));
  return Number.isFinite(number) && number > 0 ? number : 0;
}

function buildSharedQuantities(color, sizeQuantities) {
  const quantities = {};
  let totalQuantity = 0;
  for (const size of SIMULATION_SIZES) {
    const quantity = normalizeQuantity(sizeQuantities[size]);
    quantities[cellKey(color, size)] = quantity;
    totalQuantity += quantity;
  }
  return { quantities, totalQuantity };
}

function formatSizeSummary(sizeQuantities) {
  const parts = SIMULATION_SIZES
    .map((size) => [size, normalizeQuantity(sizeQuantities[size])])
    .filter(([, quantity]) => quantity > 0)
    .map(([size, quantity]) => `${size}: ${quantity}`);
  return parts.length ? parts.join(" · ") : "Sin cantidades";
}

export function SimulationAlfatexCapture({ onCancel, onCreated }) {
  const [families, setFamilies] = React.useState([]);
  const [seasons, setSeasons] = React.useState([]);
  const [family, setFamily] = React.useState("");
  const [season, setSeason] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("active");
  const [selectedColor, setSelectedColor] = React.useState("");
  const [colorSearchTerm, setColorSearchTerm] = React.useState("");
  const [sizeQuantities, setSizeQuantities] = React.useState(() => emptySizeQuantities());
  const [fillAllValue, setFillAllValue] = React.useState("");
  const [products, setProducts] = React.useState([]);
  const [selectedProductIds, setSelectedProductIds] = React.useState(() => new Set());
  const [loadingCatalogs, setLoadingCatalogs] = React.useState(true);
  const [loadingProducts, setLoadingProducts] = React.useState(false);
  const [step, setStep] = React.useState("filters");
  const [description, setDescription] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [progress, setProgress] = React.useState({ current: 0, total: 0, code: "" });
  const { colorSettings } = useColorSettings();
  const { toast } = useToast();

  const activeColors = React.useMemo(
    () =>
      Object.entries(colorSettings || {})
        .filter(([colorId, colorData]) => colorId !== "_settingsId" && colorData && colorData.active !== false)
        .sort((left, right) => (left[1]?.description || left[0]).localeCompare(right[1]?.description || right[0])),
    [colorSettings]
  );
  const hasColorCatalog = activeColors.length > 0;
  const colorSearch = colorSearchTerm.trim().toLowerCase();
  const filteredColors = React.useMemo(
    () =>
      activeColors.filter(([colorId, colorData]) =>
        colorSearch === "" ||
        colorId.toLowerCase().includes(colorSearch) ||
        (colorData?.description || "").toLowerCase().includes(colorSearch)
      ),
    [activeColors, colorSearch]
  );
  const simulationColor = trimCode(selectedColor);
  const colorLabel = colorSettings?.[simulationColor]?.description || simulationColor;
  const colorHex = colorSettings?.[simulationColor]?.hex || "#d1d5db";
  const sharedTemplate = React.useMemo(
    () => (simulationColor ? buildSharedQuantities(simulationColor, sizeQuantities) : { quantities: {}, totalQuantity: 0 }),
    [simulationColor, sizeQuantities]
  );
  const sizeSummary = React.useMemo(() => formatSizeSummary(sizeQuantities), [sizeQuantities]);

  React.useEffect(() => {
    const loadCatalogs = async () => {
      setLoadingCatalogs(true);
      const [seasonsResult, familiesResult] = await Promise.all([fetchSeasons(), fetchFamilies()]);
      if (seasonsResult.error || familiesResult.error) {
        toast({ title: "Error", description: "No se pudieron cargar temporadas o familias", variant: "destructive" });
      }
      if (!seasonsResult.error) setSeasons(seasonsResult.data || []);
      if (!familiesResult.error) setFamilies(familiesResult.data || []);
      setLoadingCatalogs(false);
    };
    loadCatalogs();
  }, [toast]);

  const loadProducts = React.useCallback(async () => {
    if (!season || !family) {
      setProducts([]);
      setSelectedProductIds(new Set());
      setLoadingProducts(false);
      return;
    }
    setLoadingProducts(true);
    setProducts([]);
    setSelectedProductIds(new Set());
    const result = await fetchAlfatexSimulationProducts({ temporada: season, codFam: family, status: statusFilter });
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudieron cargar los artículos Alfatex", variant: "destructive" });
      setLoadingProducts(false);
      return;
    }
    const rows = result.data || [];
    setProducts(rows);
    setSelectedProductIds(new Set(rows.map((product) => product.id)));
    setLoadingProducts(false);
  }, [family, season, statusFilter, toast]);

  React.useEffect(() => {
    setStep("filters");
    setDescription("");
    loadProducts();
  }, [loadProducts]);

  const selectedSeason = React.useMemo(
    () => seasons.find((item) => trimCode(item.cod_tem) === season) || null,
    [season, seasons]
  );
  const selectedFamily = React.useMemo(
    () => families.find((item) => trimCode(item.cod_fam) === family) || null,
    [family, families]
  );

  const productRows = React.useMemo(
    () =>
      products.map((product) => ({
        product,
        colors: simulationColor ? [simulationColor] : [],
        quantities: sharedTemplate.quantities,
        totalQuantity: sharedTemplate.totalQuantity,
      })),
    [products, sharedTemplate.quantities, sharedTemplate.totalQuantity, simulationColor]
  );

  const includedRows = React.useMemo(
    () => productRows.filter((row) => selectedProductIds.has(row.product.id) && row.totalQuantity > 0 && row.colors.length > 0),
    [productRows, selectedProductIds]
  );

  const totals = React.useMemo(() => ({
    articles: includedRows.length,
    quantity: includedRows.reduce((sum, row) => sum + row.totalQuantity, 0),
  }), [includedRows]);

  const allSelectableSelected = productRows.length > 0 && productRows.every((row) => selectedProductIds.has(row.product.id));
  const progressPct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;

  const toggleProduct = (productId, checked) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (checked) next.add(productId);
      else next.delete(productId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedProductIds(allSelectableSelected ? new Set() : new Set(productRows.map((row) => row.product.id)));
  };

  const handleSizeQuantityChange = (size, value) => {
    setSizeQuantities((current) => ({ ...current, [size]: value }));
  };

  const fillAllSizes = () => {
    if (fillAllValue === "") return;
    const quantity = normalizeQuantity(fillAllValue);
    setSizeQuantities(Object.fromEntries(SIMULATION_SIZES.map((size) => [size, quantity ? String(quantity) : ""])));
  };

  const goToSummary = () => {
    if (!season || !family) {
      toast({ title: "Filtros obligatorios", description: "Selecciona una temporada y una familia.", variant: "destructive" });
      return;
    }
    if (!simulationColor) {
      toast({ title: "Color obligatorio", description: "Selecciona un color para la simulación Alfatex.", variant: "destructive" });
      return;
    }
    if (sharedTemplate.totalQuantity <= 0) {
      toast({ title: "Cantidades obligatorias", description: "Introduce al menos una cantidad por talla.", variant: "destructive" });
      return;
    }
    if (!includedRows.length) {
      toast({ title: "Nada que crear", description: "No hay artículos seleccionados.", variant: "destructive" });
      return;
    }
    const seasonText = selectedSeason ? optionLabel(selectedSeason.cod_tem, selectedSeason.descrip) : season;
    const familyText = selectedFamily ? optionLabel(selectedFamily.cod_fam, selectedFamily.descrip) : family;
    setDescription((current) => current || `Muestras Alfatex ${seasonText} · ${familyText} · ${colorLabel} · ${todayLabel()}`);
    setStep("summary");
  };

  const finalEntries = includedRows.map((row) => ({
    mfgProductId: row.product.id,
    code: row.product.code,
    description: row.product.description,
    temporada: row.product.temporada || season,
    colors: row.colors,
    quantities: row.quantities,
  }));

  const createSimulation = async () => {
    if (!description.trim()) {
      toast({ title: "Descripción obligatoria", description: "Introduce una descripción para la simulación.", variant: "destructive" });
      return;
    }
    setCreating(true);
    setProgress({ current: 0, total: finalEntries.length, code: "" });
    const itemsResult = await buildForecastSimulationItems(finalEntries, { onProgress: setProgress });
    if (itemsResult.error) {
      setCreating(false);
      toast({ title: "Error", description: itemsResult.error.message || "No se pudieron calcular los costes", variant: "destructive" });
      return;
    }
    const computed = summarizeSimulationItems(itemsResult.data || []);
    if (!computed.items.length) {
      setCreating(false);
      toast({ title: "Nada que crear", description: "Los artículos seleccionados no generaron cantidades.", variant: "destructive" });
      return;
    }
    const saveResult = await saveManufacturingSimulation({
      simulationDate: today(),
      description: description.trim(),
      mode: "group",
      notes: notes.trim(),
      items: computed.items,
      totals: computed.totals,
    });
    setCreating(false);
    if (saveResult.error) {
      toast({ title: "Error", description: saveResult.error.message || "No se pudo guardar la simulación", variant: "destructive" });
      return;
    }
    toast({ title: "Simulación creada", description: `${computed.items.length} artículos · ${computed.totals.total_quantity} prendas` });
    onCreated?.();
  };

  if (loadingCatalogs) {
    return (
      <div className="flex items-center gap-2 rounded-lg border bg-white p-6 text-sm text-gray-600 shadow-sm">
        <Loader2 className="h-4 w-4 animate-spin" />Cargando temporadas y familias Alfatex...
      </div>
    );
  }

  if (creating) {
    return (
      <div className="space-y-4 rounded-lg border bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-xl font-semibold"><Loader2 className="h-5 w-5 animate-spin text-blue-600" />Creando simulación Alfatex...</h2>
        <p className="text-sm text-gray-600">Procesando {progress.total} artículos. No cierres esta ventana.</p>
        <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
          <div className="h-full rounded-full bg-blue-600 transition-all duration-200" style={{ width: `${progressPct}%` }} />
        </div>
        <p className="text-sm text-gray-700">{progress.current} / {progress.total} ({progressPct}%) {progress.code ? `· ${progress.code}` : ""}</p>
      </div>
    );
  }

  if (step === "summary") {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <h2 className="flex items-center gap-2 text-xl font-semibold"><Sparkles className="h-5 w-5 text-blue-600" />Informe de la simulación Alfatex</h2>
          <p className="text-sm text-gray-600">Revisa la plantilla común que se aplicará a todos los artículos seleccionados.</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-lg border bg-white p-4 shadow-sm"><div className="text-xs uppercase text-gray-500">Temporada</div><div className="text-lg font-bold">{selectedSeason ? optionLabel(selectedSeason.cod_tem, selectedSeason.descrip) : season}</div></div>
          <div className="rounded-lg border bg-white p-4 shadow-sm"><div className="text-xs uppercase text-gray-500">Familia</div><div className="text-lg font-bold">{selectedFamily ? optionLabel(selectedFamily.cod_fam, selectedFamily.descrip) : family}</div></div>
          <div className="rounded-lg border bg-white p-4 shadow-sm">
            <div className="text-xs uppercase text-gray-500">Color</div>
            <div className="mt-1 flex items-center gap-2 text-lg font-bold">
              <span className="h-4 w-4 rounded-full border border-gray-300" style={{ backgroundColor: colorHex }} />
              {colorLabel}
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4 shadow-sm"><div className="text-xs uppercase text-gray-500">Artículos</div><div className="text-lg font-bold">{totals.articles}</div></div>
          <div className="rounded-lg border bg-white p-4 shadow-sm"><div className="text-xs uppercase text-gray-500">Prendas</div><div className="text-lg font-bold">{totals.quantity}</div></div>
        </div>

        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold">Plantilla común de cantidades</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead className="bg-gray-50 text-center">
                <tr>{SIMULATION_SIZES.map((size) => <th key={size} className="border px-3 py-2" translate="no">{size}</th>)}<th className="border px-3 py-2">Total por artículo</th></tr>
              </thead>
              <tbody>
                <tr className="text-center">
                  {SIMULATION_SIZES.map((size) => <td key={size} className="border px-3 py-2">{normalizeQuantity(sizeQuantities[size]) || ""}</td>)}
                  <td className="border px-3 py-2 font-semibold">{sharedTemplate.totalQuantity}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium">Descripción</label><Input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Descripción de la simulación" /></div>
            <div><label className="mb-1 block text-sm font-medium">Observaciones</label><Input value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Observaciones opcionales" /></div>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold">Artículos incluidos</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead className="bg-gray-50 text-left">
                <tr><th className="border px-3 py-2">Artículo</th><th className="border px-3 py-2">Color</th><th className="border px-3 py-2">Cantidades</th><th className="border px-3 py-2 text-right">A simular</th></tr>
              </thead>
              <tbody>
                {includedRows.map((row) => (
                  <tr key={row.product.id}>
                    <td className="border px-3 py-2"><b>{row.product.code}</b> · {row.product.description}</td>
                    <td className="border px-3 py-2">{colorLabel}</td>
                    <td className="border px-3 py-2" translate="no">{sizeSummary}</td>
                    <td className="border px-3 py-2 text-right font-semibold">{row.totalQuantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setStep("filters")}><ArrowLeft className="mr-2 h-4 w-4" />Volver a filtros</Button>
          <Button type="button" onClick={createSimulation}><Sparkles className="mr-2 h-4 w-4" />Crear simulación</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-semibold"><Wand2 className="h-5 w-5 text-blue-600" />Captura mágica Alfatex</h2>
            <p className="text-sm text-gray-600">Crea una plantilla común de color y tallas para los artículos de una temporada y familia.</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel}><ArrowLeft className="mr-2 h-4 w-4" />Volver</Button>
            <Button type="button" variant="outline" onClick={loadProducts} disabled={loadingProducts || !season || !family}><RefreshCw className="mr-2 h-4 w-4" />Recargar</Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold">Familia y temporada</h3>
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_150px]">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-500">Temporada</label>
            <SeasonSelect data-season-selector="true" className="w-full rounded-md border border-input bg-background px-3 py-2" value={season} onChange={(event) => setSeason(event.target.value)}>
              <option value="">Selecciona temporada...</option>
              {seasons.map((item) => (
                <option key={item.cod_tem} value={trimCode(item.cod_tem)}>{optionLabel(item.cod_tem, item.descrip)}</option>
              ))}
            </SeasonSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-500">Familia</label>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2" value={family} onChange={(event) => setFamily(event.target.value)}>
              <option value="">Selecciona familia...</option>
              {families.map((item) => (
                <option key={item.cod_fam} value={trimCode(item.cod_fam)}>{optionLabel(item.cod_fam, item.descrip)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-500">Estado</label>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="active">Activos</option>
              <option value="obsolete">Obsoletos</option>
              <option value="all">Todos</option>
            </select>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold">Color y cantidades comunes</h3>
        <div className="grid gap-4 lg:grid-cols-[minmax(280px,420px)_1fr]">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-500">Color</label>
            {hasColorCatalog ? (
              <div className="space-y-2 rounded border bg-gray-50 p-3">
                <Input
                  value={colorSearchTerm}
                  onChange={(event) => setColorSearchTerm(event.target.value)}
                  placeholder="Buscar color..."
                  className="h-8"
                />
                {filteredColors.length === 0 ? (
                  <div className="py-3 text-center text-sm text-gray-500">
                    {colorSearch ? `No se encontraron colores que coincidan con "${colorSearchTerm}"` : "No hay colores activos disponibles."}
                  </div>
                ) : (
                  <div className="grid max-h-72 grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2 overflow-y-auto p-1">
                    {filteredColors.map(([colorId, colorData]) => {
                      const selected = selectedColor === colorId;
                      return (
                        <button
                          key={colorId}
                          type="button"
                          onClick={() => setSelectedColor(colorId)}
                          title={`${colorId}: ${colorData?.description || colorId}`}
                          className={`flex min-h-[82px] flex-col items-center gap-1 rounded-lg border bg-white p-2 ring-1 transition-all duration-200 hover:shadow-sm ${
                            selected ? "border-blue-500 ring-2 ring-blue-400" : "border-gray-200 ring-gray-200 hover:ring-2 hover:ring-blue-300"
                          }`}
                        >
                          <span
                            className="h-7 w-7 rounded-full border border-gray-300"
                            style={{ backgroundColor: colorData?.hex || "#d1d5db" }}
                          />
                          <span className="line-clamp-1 w-full text-center text-xs font-semibold text-gray-700">{colorId}</span>
                          <span className="line-clamp-2 w-full text-center text-xs text-gray-600">{colorData?.description || colorId}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <Input value={selectedColor} onChange={(event) => setSelectedColor(event.target.value)} placeholder="Color" />
            )}
            {simulationColor && (
              <div className="mt-2 flex items-center gap-2 rounded border bg-gray-50 px-3 py-2 text-sm">
                <span className="h-5 w-5 rounded-full border border-gray-300" style={{ backgroundColor: colorHex }} />
                <span>{colorLabel}</span>
              </div>
            )}
          </div>
          <div>
            <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
              <label className="block text-xs font-medium uppercase text-gray-500">Cantidad por talla</label>
              <div className="flex items-center gap-2">
                <Input type="number" min={0} value={fillAllValue} onChange={(event) => setFillAllValue(event.target.value)} placeholder="0" className="h-8 w-20 text-center" />
                <Button type="button" variant="outline" size="sm" onClick={fillAllSizes} disabled={fillAllValue === ""}>Rellenar tallas</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
              {SIMULATION_SIZES.map((size) => (
                <div key={size}>
                  <span className="mb-1 block text-center text-xs font-semibold text-gray-600" translate="no">{size}</span>
                  <Input type="number" min={0} value={sizeQuantities[size] ?? ""} onChange={(event) => handleSizeQuantityChange(size, event.target.value)} className="text-center" />
                </div>
              ))}
            </div>
            <p className="mt-2 text-right text-sm text-gray-600">Total por artículo: <b>{sharedTemplate.totalQuantity}</b></p>
          </div>
        </div>
      </div>

      {!season || !family ? (
        <div className="rounded-lg border border-dashed bg-white p-8 text-center text-sm text-gray-500">Selecciona temporada y familia para cargar artículos Alfatex.</div>
      ) : loadingProducts ? (
        <div className="flex items-center gap-2 rounded-lg border bg-white p-6 text-sm text-gray-600 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" />Cargando artículos Alfatex...
        </div>
      ) : (
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-semibold">Artículos Alfatex seleccionados</h3>
              <p className="text-sm text-gray-600">La misma plantilla de color y tallas se aplicará a cada artículo marcado.</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex gap-4 text-sm">
                <span className="text-gray-600">Artículos: <b>{totals.articles}</b></span>
                <span className="text-gray-600">Prendas: <b>{totals.quantity}</b></span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={toggleAll}>{allSelectableSelected ? "Deseleccionar" : "Seleccionar"} todo</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="w-12 border px-3 py-2"></th>
                  <th className="border px-3 py-2">Artículo</th>
                  <th className="border px-3 py-2">Color</th>
                  <th className="border px-3 py-2">Cantidades</th>
                  <th className="border px-3 py-2 text-right">A simular</th>
                  <th className="border px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {productRows.map((row) => {
                  const selected = selectedProductIds.has(row.product.id);
                  return (
                    <tr key={row.product.id} className={selected ? "bg-white" : "bg-gray-50 text-gray-400"}>
                      <td className="border px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={selected}
                          onChange={(event) => toggleProduct(row.product.id, event.target.checked)}
                        />
                      </td>
                      <td className="border px-3 py-2"><b>{row.product.code}</b> · {row.product.description}</td>
                      <td className="border px-3 py-2">{simulationColor ? colorLabel : ""}</td>
                      <td className="border px-3 py-2" translate="no">{sizeSummary}</td>
                      <td className="border px-3 py-2 text-right font-semibold">{selected ? row.totalQuantity : 0}</td>
                      <td className="border px-3 py-2 text-xs">{row.product.status || ""}</td>
                    </tr>
                  );
                })}
                {!productRows.length && <tr><td className="border px-3 py-6 text-center text-gray-500" colSpan={6}>No hay artículos Alfatex para esta temporada y familia.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}><ArrowLeft className="mr-2 h-4 w-4" />Cancelar</Button>
        <Button type="button" onClick={goToSummary} disabled={!season || !family || loadingProducts || !productRows.length || selectedProductIds.size === 0}><Sparkles className="mr-2 h-4 w-4" />Ver informe</Button>
      </div>
    </div>
  );
}
